import { useEffect, useMemo, useState } from "react";
import { Video } from "lucide-react";
import { format } from "date-fns";
import { BackButton } from "@/components/owner/back-button";
import { ProfileBadge } from "@/components/ui/profile-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ClassVideoCard } from "@/components/videos/class-video-card";
import { useAuth } from "@/lib/contexts/auth-context";
import { supabase } from "@/lib/supabase";
import { parseLocalDate } from "@/lib/utils/class-time-utils";
import { getClassVideosSummary } from "@/lib/api/class-videos";
import type { ClassVideoSummary } from "@/types/class-video-summary";

interface ProfileClassVideosPageProps {
  username: string;
}

export function ProfileClassVideosPage({ username }: ProfileClassVideosPageProps) {
  const { user } = useAuth();

  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [profileUsername, setProfileUsername] = useState<string | null>(null);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassVideoSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Resolve username → profileId
  useEffect(() => {
    const resolve = async () => {
      setLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from("profiles")
          .select("id, full_name, username, profile_photo")
          .ilike("username", username)
          .single();

        if (fetchError || !data) {
          setError(true);
          return;
        }

        setProfileId(data.id);
        setProfileName(data.full_name);
        setProfileUsername(data.username);
        setProfilePhoto(data.profile_photo);
      } catch {
        setError(true);
      }
    };

    resolve();
  }, [username]);

  // Fetch class videos summary once profileId is resolved
  useEffect(() => {
    if (!profileId) return;

    const fetchSummary = async () => {
      try {
        const summary = await getClassVideosSummary(profileId);
        setClasses(summary);
      } catch (err) {
        console.error("Error fetching class videos summary:", err);
        setClasses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [profileId]);

  const isOwner = !!user && !!profileId && user.id === profileId;

  // For public view: only show classes that have videos or external links
  // For owner view: show all past classes so they can add videos
  const visibleClasses = useMemo(() => {
    if (isOwner) return classes;
    return classes.filter((c) => c.total_video_count > 0 || !!c.external_video_url);
  }, [classes, isOwner]);

  // Group classes by month/year (data is already sorted date DESC from backend)
  const groupedClasses = useMemo(() => {
    const groups: Array<{ label: string; classes: ClassVideoSummary[] }> = [];
    let currentLabel = "";

    for (const cls of visibleClasses) {
      const date = parseLocalDate(cls.date);
      const label = format(date, "MMMM yyyy");

      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, classes: [] });
      }
      groups[groups.length - 1].classes.push(cls);
    }

    return groups;
  }, [visibleClasses]);

  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <p className="text-center text-on-light-muted py-20">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-on-light">Class Videos</h1>
        {profileName && (
          <div className="mt-1">
            <ProfileBadge
              username={profileUsername}
              fullName={profileName}
              profilePhoto={profilePhoto}
              variant="on-light"
            />
          </div>
        )}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="space-y-8">
          <div>
            <Skeleton className="h-5 w-32 mb-3" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="aspect-video w-full rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      ) : visibleClasses.length === 0 ? (
        /* Empty state */
        <div className="text-center py-20">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
            <Video className="h-8 w-8 text-on-light-muted" />
          </div>
          <h3 className="text-lg font-semibold mb-2 text-on-light">No Class Videos Yet</h3>
          <p className="text-on-light-muted">
            {isOwner
              ? "Upload videos or add links from your class management page."
              : "This instructor hasn't shared any class videos yet."}
          </p>
        </div>
      ) : (
        /* Grouped grid */
        <div className="space-y-8">
          {groupedClasses.map((group) => (
            <div key={group.label}>
              <h2 className="text-on-light-muted text-xs uppercase tracking-wider font-medium mb-3">
                {group.label}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                {group.classes.map((cls) => (
                  <ClassVideoCard
                    key={cls.id}
                    data={cls}
                    isOwner={isOwner}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
